import PropTypes from 'prop-types';
import React, { memo } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { Link } from 'react-router-dom';

import DropDown, { DropDownElement } from '../helpers/DropDown';
import DashboardPanel from './DashboardPanel';
import MainHeader from './MainHeader';
import { AppPage } from '../AppPage';
import Button from '../helpers/Button';

import style from './style.module.css';

/**
 * Home page: app header, search bar and the dashboard widgets.
 *
 * @param props {Object}.
 * @param props.title {String} Saritasa user at example.
 * @param props.controls {Immutable.Map} Map with search bar and dropdown (show/hide panels) definition.
 * @param props.widgets {Immutable.Map} Widgets definition (shown, cords, data, ...).
 */
const Main = props => {
  const {
    isDisabledSearch,
    user,
    serverSideSortingTables,
    controls,
    widgets,
    onRowDoubleClick,
    onCellDoubleClick,
    onDragStart,
    onDragEnd,
    onGetNextPageData,
    onSort,
    onResizeEnd,
    onWidgetClick,
    onWidgetToggle,
    searchText,
    onSearchChange,
    onSearch,
    triggerVisibility,
    onCCVisitsReport,
    onRetainerReportClick,
    onOutreachReport,
    onCeoOutreachReport,
    onOffersAndBetterClick,
    onLeadsAndBetterClick,
    widgetsOrder,
    onColumnResize,
    onActivityReportClick,
    onChangeCheckboxId,
    onChangeAllPD,
    onConfirmDeletion,
    onConfirmReject,
    onConflictReportClick,
    onRetainerHealthReportClick,
    onUpdateSaveQuery,
    onRunQuery,
    onOpenReport,
    onRunReport,
    onScraperExtensionClick,
    onDeleteSavedQuery,
    onDeleteMeeting,
    onViewMeetingDetailsNote,
  } = props;

  const searchButtonText = controls.getIn(['search', 'buttonText']);
  const searchInputText = controls.getIn(['search', 'inputText']);
  const content = [];

  widgets.map((widget, key) =>
    content.push(
      <DashboardPanel
        key={key}
        isDataFiltered={props.isDataFiltered}
        name={key}
        onCellDoubleClick={onCellDoubleClick}
        onChangeAllPD={onChangeAllPD}
        onChangeCheckboxId={onChangeCheckboxId}
        onClick={onWidgetClick}
        onClose={triggerVisibility}
        onColumnResize={onColumnResize}
        onConfirmDeletion={onConfirmDeletion}
        onConfirmReject={onConfirmReject}
        onDeleteMeeting={onDeleteMeeting}
        onDeleteSavedQuery={onDeleteSavedQuery}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        onGetNextPageData={page => onGetNextPageData(page, widget.get('portlet'))}
        onOpenReport={onOpenReport}
        onResizeEnd={onResizeEnd}
        onRowDoubleClick={onRowDoubleClick}
        onRunQuery={onRunQuery}
        onRunReport={onRunReport}
        onSort={onSort}
        onSwitchFilterData={props.onSwitchFilterData}
        onToggle={onWidgetToggle}
        onUpdateSaveQuery={onUpdateSaveQuery}
        onViewMeetingDetailsNote={onViewMeetingDetailsNote}
        serverSideSortingTables={serverSideSortingTables}
        widget={widget}
        zIndex={widgetsOrder.indexOf(key) + 20}
      />,
    ),
  );

  const togglerButtonText = controls.getIn(['dropdown', 'text']);
  const userRole = user.getIn(['roles', 0, 'slug'], null);

  const togglerContent = controls
    .getIn(['dropdown', 'elements'])
    .map(element => {
      const widget = element.get('key');
      const access = element.get('access');
      const attributes = element.toJS();
      const className = classNames({
        'text-bold': widgets.getIn([widget, 'visible']),
      });

      // check if a user has access to this menu item
      if (access && !access.includes(userRole)) {
        return '';
      }

      return (
        <DropDownElement {...attributes} key={widget} name={widget} onClick={event => triggerVisibility(event, widget)}>
          <span className={className}>{element.get('name')}</span>
        </DropDownElement>
      );
    })
    .toJS();

  const handleKeyUp = event => {
    if (isDisabledSearch) {
      return;
    }

    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const title = `${user.get('firstName', '')} ${user.get('lastName', '')}`;
  let browsLink = null;
  let widgetsDropDown = null;

  if (props.hasAccess) {
    browsLink = (
      <Link className={style.searchLink} target="_blank" to="browse">
        browse/query
      </Link>
    );
    widgetsDropDown = (
      <DropDown align="right" buttonContent={togglerButtonText} className="btn-default btn-xs" id="toggleDashboard">
        {togglerContent}
      </DropDown>
    );
  }

  const toolbar = (
    <>
      <div className={classNames(style.search, 'input-group')}>
        <input
          className="form-control"
          name="searchText"
          onChange={onSearchChange}
          onKeyUp={handleKeyUp}
          placeholder={searchInputText}
          type="text"
          value={searchText}
        />
        <span className="input-group-btn">
          <Button className="btn-primary" disabled={isDisabledSearch} onClick={onSearch}>
            <i className="fa fa-search" /> {searchButtonText}
          </Button>
        </span>
      </div>
      {browsLink}
      <div className={style.actions}>
        <MainHeader
          onActivityReportClick={onActivityReportClick}
          onCCVisitsReport={onCCVisitsReport}
          onCeoOutreachReport={onCeoOutreachReport}
          onConflictReportClick={onConflictReportClick}
          onLeadsAndBetterClick={onLeadsAndBetterClick}
          onOffersAndBetterClick={onOffersAndBetterClick}
          onOpenReport={onOpenReport}
          onOutreachReport={onOutreachReport}
          onRetainerHealthReportClick={onRetainerHealthReportClick}
          onRetainerReportClick={onRetainerReportClick}
          onRunQuery={onRunQuery}
          onRunReport={onRunReport}
          onScraperExtensionClick={onScraperExtensionClick}
          userRole={user.getIn(['roles', 0])}
        />
        {widgetsDropDown}
      </div>
    </>
  );

  return (
    <AppPage overline="Home" title={title} toolbar={toolbar}>
      <div className={style.host} role="main">
        {content}
      </div>
    </AppPage>
  );
};

Main.propTypes = {
  controls: PropTypes.instanceOf(Map).isRequired,
  onActivityReportClick: PropTypes.func.isRequired,
  onChangeCheckboxId: PropTypes.func.isRequired,
  onConflictReportClick: PropTypes.func.isRequired,
  widgets: PropTypes.instanceOf(Map).isRequired,
};

export default memo(Main);
